import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import styles from './Frequency.module.scss';

import HelpBox from '../../components/HelpBox/HelpBox';
import PageWrapper from '../../components/PageWrapper/PageWrapper';
import ProgressButtons from '../../components/Progress/ProgressButtons/ProgressButtons';
import ProgressBar from '../../components/Progress/ProgressBar/ProgressBar';
import RMLConnection from '../../components/RMLConnection/RMLConnection';
import SelectableGrid from '../../components/SelectableGrid/SelectableGrid';
import {
  getSelectedDay,
  getSelectedMonths,
  getSelectedDistribution,
  getSelectedDestination,
} from '../../redux/modules/shoppingCart/shoppingCart.selectors';
import {
  FREQUENCY_STEP,
  TAX_WITHHOLDING_STEP,
  TOLL_FREE_NUMBER,
  selectableMonths,
  selectableDays,
  REINVEST_STEP,
  DISTRIBUTION_METHOD_STEP,
  REINVEST_TO_NEW,
} from '../../settings/settings';
import {
  getDisabledDays,
  getHeaderDisabledText,
  isDisabledDay,
  isValidSelection,
} from '../../utils/date-utils';
import { getRMDfunds } from '../../redux/modules/UserFunds/userFunds.selectors';
import { getHouseholdSegmentInformation } from '../../redux/modules/PersonalInformation/personalInformation.selectors';

const Frequency = (props) => {
  const currentSelectedMonth = useSelector(getSelectedMonths);
  const currentSelectedDay = useSelector(getSelectedDay);
  const [selectedMonths, setSelectedMonths] = useState(
    useSelector(getSelectedMonths)
  );
  const [selectedDay, setSelectedDays] = useState(useSelector(getSelectedDay));
  const initialDateInvalid = useSelector(
    (state) => state.shoppingCart.invalidDay
  );
  const [showInitDateValid, setShowInitDateValid] = useState(
    selectedDay.length === 0 && initialDateInvalid
  );
  const selectedDistribution = useSelector(getSelectedDistribution);
  const selectedDestination = useSelector(getSelectedDestination);
  const { allFunds: fundList } = useSelector(getRMDfunds);
  /* Getting household entity segment information */
  const enableDistributionMethod = useSelector(getHouseholdSegmentInformation);
  /* Navigate to Multiple funds in distribution page when the user is in Enhanced Personal Services */
  const showCustomDistributionMethod =
    !enableDistributionMethod || fundList.length === 1;
  function selectAllMonths() {
    if (selectedMonths.length === 12) {
      setSelectedMonths([]);
    } else {
      /* If the currently selected day interferes with a potential ineligible */
      /* day for all months, it will be unselected.                           */
      if (selectedDay.length > 0) {
        let day = selectedDay[0].value;
        if ((day >= 1 && day <= 5) || (day >= 25 && day <= 31)) {
          setSelectedDays([]);
        }
      }
      setSelectedMonths(selectableMonths);
    }
  }

  function onSelect(type, option, monthArr, dayArr) {
    if (type === 'month') {
      let monthTemp = monthArr;

      /* Deselecting a MONTH */
      if (monthTemp.indexOf(option) !== -1) {
        setSelectedMonths(monthTemp.filter((a) => a !== option));
        /* Selecting a MONTH */
      } else {
        /* Selecting a MONTH while a DAY is selected (day could be ineligible) */
        if (dayArr.length > 0) {
          let month = option.label;
          let monthVal = option.value;
          let day = dayArr[0].value;

          if (isDisabledDay(month, day, monthVal)) {
            setSelectedDays([]);
          }
        }
        setSelectedMonths([...monthTemp, option]);
      }
    } else if (type === 'day') {
      /* Deselecting a DAY */
      if (dayArr.length > 0 && option.value === dayArr[0].value) {
        setSelectedDays([]);
        /* Selecting a DAY */
      } else {
        setShowInitDateValid(false);
        setSelectedDays([option]);
      }
    }
  }

  return (
    <div className="rmd-distribution-wrapper">
      <div className="sticky-header">
        <div className="heading">Auto-RMD Setup</div>
        <ProgressButtons
          edit={props.edit}
          currentStep={FREQUENCY_STEP}
          currentSelection={{
            months: currentSelectedMonth,
            day: currentSelectedDay,
          }}
          payload={{
            months: selectedMonths,
            day: selectedDay,
          }}
          nextStep={TAX_WITHHOLDING_STEP}
          isSelectedPastDate={
            !props.edit &&
            selectedMonths.every((month) =>
              selectedDay.every(
                (date) =>
                  new Date() >
                  new Date(new Date().getFullYear(), month.value, date.value)
              )
            )
          }
          previousStep={
            selectedDestination === REINVEST_STEP ||
            selectedDestination === REINVEST_TO_NEW
              ? selectedDestination
              : showCustomDistributionMethod
              ? DISTRIBUTION_METHOD_STEP
              : selectedDistribution
          }
          {...props}
          showBackButton={true}
          backDisabled={false}
          disabled={!isValidSelection(selectedMonths, selectedDay)}
        >
          <ProgressBar completedSteps={[0, 1]} activeIndex={2} />
        </ProgressButtons>
      </div>
      <PageWrapper nextStep={FREQUENCY_STEP} {...props}>
        <div className="scroll-wrapper">
          <div className="rmd-title">
            When do you want to withdraw your RMD?
          </div>
          <div className={styles['option-container']}>
            <SelectableGrid
              chartType={'month'}
              headerTitle={'Months'}
              headerLimitText={'(Select at least one)'}
              options={selectableMonths}
              selectedOptions={selectedMonths}
              onSelect={(option) =>
                onSelect('month', option, selectedMonths, selectedDay)
              }
              selectAll={true}
              toggleSelectAll={selectAllMonths}
              highlightToday
            />
            <SelectableGrid
              chartType={'day'}
              headerTitle={'Date'}
              headerLimitText={'(Select one)'}
              headerDisabledText={getHeaderDisabledText(selectedMonths)}
              options={selectableDays}
              selectedOptions={selectedDay}
              disabledOptions={getDisabledDays(selectedMonths)}
              onSelect={(option) =>
                onSelect('day', option, selectedMonths, selectedDay)
              }
              selectAll={false}
              initialDateInvalid={showInitDateValid}
              highlightToday={
                !!(
                  !selectedMonths?.length ||
                  selectedMonths?.find(
                    (month) => month.value === new Date().getMonth()
                  )
                )
              }
            />
            <div className={styles['freq-legend-container']}>
              <p className={styles['cal-legend']}>Calendar Legend</p>
              <div className={styles['legend-inpt-container']}>
                <div className={styles['legend-inpt-div']}>
                  <span type="checkbox" className={styles['current-date']} />{' '}
                  <span className={styles['legend-txt']}>Current Date</span>
                </div>
                <div className={styles['legend-inpt-div']}>
                  <span type="checkbox" className={styles['available-dates']} />{' '}
                  <span className={styles['legend-txt']}>Available dates</span>
                </div>
                <div className={styles['legend-inpt-div']}>
                  <span
                    type="checkbox"
                    className={styles['unavaiable-dates']}
                  />{' '}
                  <span className={styles['legend-txt']}>
                    Unavailable dates
                  </span>
                </div>
              </div>
            </div>
            <div className={styles['tell-me-more-container']}>
              <HelpBox step={FREQUENCY_STEP} />
            </div>
            <RMLConnection phoneNumber={TOLL_FREE_NUMBER} />
          </div>
        </div>
      </PageWrapper>
    </div>
  );
};

export default Frequency;
